import React, { useState } from 'react';
import { Briefcase, DollarSign, Activity, Heart, BookOpen, Smile, Home, Users } from 'lucide-react';

// Category configuration with icons and colors
const WHEEL_CATEGORIES = [
  { name: 'Career', icon: Briefcase, color: '#C59D52', description: 'Professional growth and career satisfaction' },
  { name: 'Financial', icon: DollarSign, color: '#b8935f', description: 'Financial security and money management' },
  { name: 'Health', icon: Activity, color: '#a87f52', description: 'Physical health and fitness' },
  { name: 'Relationships', icon: Heart, color: '#9b7c54', description: 'Romantic relationships and intimacy' },
  { name: 'Personal Growth', icon: BookOpen, color: '#8d6e47', description: 'Learning, development, and self-improvement' },
  { name: 'Fun & Recreation', icon: Smile, color: '#7a5c36', description: 'Leisure, hobbies, and enjoyment' },
  { name: 'Physical Environment', icon: Home, color: '#C59D52', description: 'Home, workspace, and surroundings' },
  { name: 'Family & Friends', icon: Users, color: '#b8935f', description: 'Family bonds and friendships' }
];

function WheelScoreEditor({ scores, onScoresChange, readonly = false }) {
  const [expandedCategory, setExpandedCategory] = useState(null);

  const handleScoreChange = (category, newScore) => {
    const updatedScores = scores.map(score =>
      score.category === category
        ? { ...score, score: parseInt(newScore) }
        : score
    );
    onScoresChange(updatedScores);
  };

  const handleNotesChange = (category, newNotes) => {
    const updatedScores = scores.map(score =>
      score.category === category
        ? { ...score, notes: newNotes }
        : score
    );
    onScoresChange(updatedScores);
  };

  const handleFieldChange = (category, field, value) => {
    const updatedScores = scores.map(score =>
      score.category === category
        ? { ...score, [field]: value }
        : score
    );
    onScoresChange(updatedScores);
  };

  return (
    <div className="space-y-3">
      {WHEEL_CATEGORIES.map((categoryConfig) => {
        const score = scores.find(s => s.category === categoryConfig.name) || {
          category: categoryConfig.name,
          score: 5,
          notes: '',
          whatsWorking: '',
          challenges: '',
          ideal: ''
        };
        const Icon = categoryConfig.icon;
        const isExpanded = expandedCategory === categoryConfig.name;

        return (
          <div
            key={categoryConfig.name}
            className="bg-white/5 backdrop-blur-xl rounded-xl border border-white/10 overflow-hidden transition-all"
          >
            {/* Category Header */}
            <div
              className="p-4 cursor-pointer hover:bg-white/5 transition-colors"
              onClick={() => setExpandedCategory(isExpanded ? null : categoryConfig.name)}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3 flex-1">
                  <div
                    className="w-10 h-10 rounded-lg flex items-center justify-center"
                    style={{
                      backgroundColor: `${categoryConfig.color}20`,
                      color: categoryConfig.color
                    }}
                  >
                    <Icon className="w-5 h-5" />
                  </div>
                  <div className="flex-1">
                    <h4 className="font-medium" style={{ color: 'var(--text-primary)' }}>{categoryConfig.name}</h4>
                    <p className="text-xs mt-0.5" style={{ color: 'var(--text-tertiary)' }}>{categoryConfig.description}</p>
                  </div>
                </div>

                {/* Score display */}
                <div className="flex items-center gap-4">
                  <div className="text-right">
                    <p className="text-2xl font-bold" style={{ color: categoryConfig.color }}>
                      {score.score}
                    </p>
                    <p className="text-xs" style={{ color: 'var(--text-tertiary)' }}>/10</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Expanded Content */}
            {isExpanded && (
              <div className="px-4 pb-4 space-y-4 border-t border-white/10">
                {/* Slider */}
                <div className="pt-4">
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                      How satisfied are you? (0 = Not at all, 10 = Completely)
                    </label>
                    <span className="text-lg font-bold" style={{ color: categoryConfig.color }}>
                      {score.score}
                    </span>
                  </div>

                  <div className="relative">
                    <input
                      type="range"
                      min="0"
                      max="10"
                      value={score.score}
                      onChange={(e) => !readonly && handleScoreChange(categoryConfig.name, e.target.value)}
                      disabled={readonly}
                      className="w-full h-2 rounded-lg appearance-none cursor-pointer"
                      style={{
                        background: `linear-gradient(to right, ${categoryConfig.color} 0%, ${categoryConfig.color} ${score.score * 10}%, rgba(255,255,255,0.1) ${score.score * 10}%, rgba(255,255,255,0.1) 100%)`
                      }}
                    />
                    <div className="flex justify-between mt-1 px-1">
                      {[0, 2, 4, 6, 8, 10].map((mark) => (
                        <span key={mark} className="text-xs" style={{ color: 'var(--text-tertiary)' }}>
                          {mark}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Three required questions */}
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm mb-2" style={{ color: 'var(--text-secondary)' }}>
                      What's working in this area? <span className="text-red-400">*</span>
                    </label>
                    <textarea
                      value={score.whatsWorking || ''}
                      onChange={(e) => !readonly && handleFieldChange(categoryConfig.name, 'whatsWorking', e.target.value)}
                      disabled={readonly}
                      placeholder="Describe what's going well and what you're satisfied with..."
                      className="w-full px-3 py-2 rounded-lg border focus:outline-none focus:ring-2 focus:ring-[#C59D52] resize-none text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                      style={{ backgroundColor: 'var(--bg-secondary)', color: 'var(--text-primary)', borderColor: 'var(--border-color)' }}
                      rows="3"
                    />
                  </div>

                  <div>
                    <label className="block text-sm mb-2" style={{ color: 'var(--text-secondary)' }}>
                      What challenges are you facing? <span className="text-red-400">*</span>
                    </label>
                    <textarea
                      value={score.challenges || ''}
                      onChange={(e) => !readonly && handleFieldChange(categoryConfig.name, 'challenges', e.target.value)}
                      disabled={readonly}
                      placeholder="What obstacles or difficulties are you experiencing..."
                      className="w-full px-3 py-2 rounded-lg border focus:outline-none focus:ring-2 focus:ring-[#C59D52] resize-none text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                      style={{ backgroundColor: 'var(--bg-secondary)', color: 'var(--text-primary)', borderColor: 'var(--border-color)' }}
                      rows="3"
                    />
                  </div>

                  <div>
                    <label className="block text-sm mb-2" style={{ color: 'var(--text-secondary)' }}>
                      What would a 10 look like for you? <span className="text-red-400">*</span>
                    </label>
                    <textarea
                      value={score.ideal || ''}
                      onChange={(e) => !readonly && handleFieldChange(categoryConfig.name, 'ideal', e.target.value)}
                      disabled={readonly}
                      placeholder="Describe your ideal state for this area of your life..."
                      className="w-full px-3 py-2 rounded-lg border focus:outline-none focus:ring-2 focus:ring-[#C59D52] resize-none text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                      style={{ backgroundColor: 'var(--bg-secondary)', color: 'var(--text-primary)', borderColor: 'var(--border-color)' }}
                      rows="3"
                    />
                  </div>
                </div>

                {/* Optional notes */}
                <div>
                  <label className="block text-sm mb-2" style={{ color: 'var(--text-secondary)' }}>
                    Additional notes (optional)
                  </label>
                  <textarea
                    value={score.notes || ''}
                    onChange={(e) => !readonly && handleNotesChange(categoryConfig.name, e.target.value)}
                    disabled={readonly}
                    placeholder="Any other thoughts or context..."
                    className="w-full px-3 py-2 rounded-lg border focus:outline-none focus:ring-2 focus:ring-[#C59D52] resize-none text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{ backgroundColor: 'var(--bg-secondary)', color: 'var(--text-primary)', borderColor: 'var(--border-color)' }}
                    rows="2"
                  />
                </div>
              </div>
            )}
          </div>
        );
      })}

      {/* Quick action buttons */}
      {!readonly && (
        <div className="flex gap-2 pt-4">
          <button
            onClick={() => onScoresChange(WHEEL_CATEGORIES.map(cat => ({
              category: cat.name,
              score: 5,
              notes: '',
              whatsWorking: '',
              challenges: '',
              ideal: ''
            })))}
            className="flex-1 px-4 py-2 rounded-lg text-sm transition-all"
            style={{
              backgroundColor: 'var(--bg-tertiary)',
              color: 'var(--text-secondary)',
              borderWidth: '1px',
              borderStyle: 'solid',
              borderColor: 'var(--border-color)'
            }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--bg-secondary)'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'var(--bg-tertiary)'}
          >
            Reset All to 5
          </button>
          <button
            onClick={() => setExpandedCategory(null)}
            className="flex-1 px-4 py-2 rounded-lg text-sm transition-all"
            style={{
              backgroundColor: 'var(--bg-tertiary)',
              color: 'var(--text-secondary)',
              borderWidth: '1px',
              borderStyle: 'solid',
              borderColor: 'var(--border-color)'
            }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--bg-secondary)'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'var(--bg-tertiary)'}
          >
            Collapse All
          </button>
        </div>
      )}
    </div>
  );
}

export default WheelScoreEditor;
